import React from 'react';
import { withQueryProvider, extend } from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ActiveOrdersView } from './ActiveOrdersView';
import { useActiveOrders, activeOrdersDataModel } from '../hooks/useActiveOrders';
import { orderDetailsDataModel } from '../hooks/useOrderDetails';
import { CUSTOMER_TYPES } from '../constants';
import { IActiveDistribution } from '../models/active-orders';

export interface IActiveOrdersProps {
  /** Overrides the customer type from the signed-in session. */
  customerType?: string;
  /** Defaults to 1. */
  pageNumber?: number;
  /** Defaults to 20. */
  pageSize?: number;
  onCardClick?: (distribution?: IActiveDistribution) => void;
  componentLocation?: string;
  /** Renders content-only: no title/icon header, no outer Card border. Defaults to false. */
  isDashboardSnapshot?: boolean;
}

/**
 * Active Orders for the signed-in customer.
 *
 * Fetches its own data, so an experience can drop it on a page without wiring
 * anything up. Loading, error, and empty states are handled internally.
 */
const ActiveOrders = ({
  customerType = '',
  pageNumber = undefined,
  pageSize = undefined,
  onCardClick = undefined,
  componentLocation = 'default',
  isDashboardSnapshot = false,
}: IActiveOrdersProps) => {
  const { isB2BCustomer } = useThdCustomer() ?? {};
  const {
    data, loading, error, refetch, skipped
  } = useActiveOrders({ pageNumber, pageSize });

  const resolvedCustomerType = customerType
    || (isB2BCustomer ? CUSTOMER_TYPES.B2B : CUSTOMER_TYPES.B2C);

  return (
    <ActiveOrdersView
      // The response goes straight to the view. The cards and drawer read the
      // API shape, so there is nothing to convert.
      data={data ?? {}}
      customerType={resolvedCustomerType}
      // A skipped query never resolves, so it must not read as loading.
      isLoading={!skipped && loading}
      isError={Boolean(error)}
      onRetry={refetch}
      onCardClick={onCardClick}
      componentLocation={componentLocation}
      isDashboardSnapshot={isDashboardSnapshot}
    />
  );
};

ActiveOrders.defaultProps = {
  customerType: '',
  pageNumber: undefined,
  pageSize: undefined,
  onCardClick: undefined,
  componentLocation: 'default',
  isDashboardSnapshot: false,
};

// `QueryProvider` resolves the queries it can serve by statically walking the
// JSX children it is handed, reading `.dataModel` off each element's type. It
// never renders anything to discover them. `FulfillmentDetailsDrawer` fetches
// `orderDetails` several rendered layers down, so its own `dataModel` static is
// invisible to that walk and its query would fail to resolve. Merge it here.
const composedDataModel = extend(activeOrdersDataModel, orderDetailsDataModel);

ActiveOrders.dataModel = composedDataModel;

const wrappedActiveOrders = withQueryProvider(ActiveOrders, {
  cacheKey: 'activeOrders',
});

// `withQueryProvider` copies `dataModel` onto its inner clone but not onto the
// component it returns. Re-attach it so an experience can still hoist the query
// with `extend({}, ActiveOrders)` if it wants to.
(wrappedActiveOrders as any).dataModel = composedDataModel;
(wrappedActiveOrders as any).displayName = 'ActiveOrders';

export {
  wrappedActiveOrders as ActiveOrders,
  // Unwrapped so `mockRequestResponse` can read `.dataModel` off it. Render the
  // wrapped one; this export is for building mocks.
  ActiveOrders as __unwrappedActiveOrders,
};
